import {
	inject,
	Injectable,
	Injector
} from '@angular/core';

import { EnvironmentConfig } from 'src/environments/environment-config';

import {
	ConfigurationManagerService,
	EntityManagerService,
	EnvironmentInfoService,
	UserActiveSessionManagerService
} from 'src/app/core';

import { datadogLogs } from '@datadog/browser-logs';
import { restManager } from 'app/ajs-upgraded-providers';
import { upperFirst } from 'src/app/shared/string-utils';
import { User } from 'src/app/models';

@Injectable({
	providedIn: 'root'
})
export class ErrorsLoggerService {
	private inject = inject(Injector);
	private environmentInfo = inject(EnvironmentInfoService);
	private userActiveSession = inject(UserActiveSessionManagerService);

	constructor(
	) {
		datadogLogs.setLoggerGlobalContext(this.getLogContext());
	}

	private getLogContext(sessionData?: any) {
		let logContext: any = {
			client: EnvironmentConfig.clientId,
			env: EnvironmentConfig.environment
		};

		if (sessionData) {
			Object.assign(logContext, {
				shop: sessionData.shop?.name,
				username: sessionData.username,
			});
		}

		return logContext;
	};

	public err(...args: any[]) {
		return console.error(...args);
	}

	public warn(...args: any[]) {
		return console.warn(...args);
	}

	public info(...args: any[]) {
		return console.info(...args);
	}

	public debug(...args: any[]) {
		return console.debug(...args);
	}

	public async sendReport(message: any, options?: any) {
		if (typeof options !== 'object' || options === null) {
			options = {};
		}

		let messagePayload: any;

		try {
			messagePayload = JSON.parse(JSON.stringify(message));
		} catch (error) {
			messagePayload = structuredClone(message);
		}

		const deviceMeta = await this.environmentInfo.getDeviceMeta();

		Object.assign(messagePayload, deviceMeta, { timestamp: new Date().getTime() });

		try {
			let netInfo = await this.environmentInfo.getNetworkInfo();
			Object.assign(messagePayload, netInfo);
		} catch (err) {
			// Nothing to do
		} finally {
			if (options.skipStorage) {
				await this.inject.get(restManager).post('application_reports', messagePayload);
			} else {
				await this.inject.get(EntityManagerService).applicationReports.postOneOfflineFirst(messagePayload);
			}
		}
	};

	public async sendAudit(type: string, data?: any) {
		const payload = (data && typeof data === 'object') ? data : {};

		return this.sendReport({
			...payload,
			type: `audit${upperFirst(type)}`,
			user: (({ id, first_name, last_name, username }) => ({ id, first_name, last_name, username }))(this.userActiveSession.getSession() || {} as User)
		});
	};

	public setContext(sessionData: any) {
		datadogLogs.setLoggerGlobalContext(this.getLogContext(sessionData));
	};

	public setupLogCollection() {
		const configurationManager = this.inject.get(ConfigurationManagerService);
		const logToHttp = configurationManager.getSetting('logging.enable_log_collection');
		const loggingLevel = configurationManager.getSetting('logging.min_logging_level') || (['unstable', 'local', 'beta'].includes(EnvironmentConfig.environment) ? 'debug' : 'info');

		datadogLogs.logger.setHandler(logToHttp ? ['http', 'console'] : 'console');
		datadogLogs.logger.setLevel(loggingLevel);
	};
}